import React, { useEffect, useState } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { SampleDownload } from "./LeadShareDownload";

const show = (v) => {
  if (v === null || v === undefined) return "null";
  if (v === "") return '""';
  if (Array.isArray(v)) return v.length ? v.join(", ") : "[]";
  if (typeof v === "object") return Object.entries(v).map(([k, x]) => `${k}: ${x}`).join("; ") || "{}";
  return String(v);
};

function Row({ r }) {
  const changed = !r.key || r.name !== r.key;
  return (
    <div className="grid grid-cols-[45%_1fr] gap-2 px-3 py-1" data-testid={`lead-share-preview-row-${r.key || `custom-${r.name}`}`}>
      <dt className="min-w-0">
        <div className="text-zinc-600">{r.label}</div>
        <div className={`font-mono text-[10px] [overflow-wrap:anywhere] ${changed ? "bg-yellow-100 font-bold text-black" : "text-zinc-400"}`}
             data-testid={`lead-share-preview-name-${r.key || `custom-${r.name}`}`}>{r.name}</div>
      </dt>
      <dd className={`min-w-0 font-mono text-[11px] [overflow-wrap:anywhere] ${r.omitted ? "italic text-zinc-400" : ""}`}
          data-testid={`lead-share-preview-value-${r.key || `custom-${r.name}`}`}>{r.omitted ? "left out (empty)" : show(r.value)}</dd>
    </div>
  );
}

// A sample lead run through the real sending rules, so this is exactly what the buyer's system gets.
export function LeadSharePreview({ rule, layer, destId }) {
  const [data, setData] = useState(null);
  const sig = JSON.stringify({ hidden: [...rule.hidden].sort(), names: rule.names, custom_fields: rule.custom_fields,
    formats: rule.formats || {}, field_formats: rule.field_formats || {}, layer_default: layer });
  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/admin/lead-share/preview", JSON.parse(sig)).then((r) => setData(r.data)).catch(() => setData(null));
    }, 300);
    return () => clearTimeout(t);
  }, [sig]);
  return (
    <aside className="min-w-0 self-start border-2 border-black bg-white lg:sticky lg:top-4" data-testid="lead-share-preview">
      <div className="bg-black px-3 py-2 text-white">
        <div className="text-[11px] font-bold uppercase tracking-widest">What the buyer gets</div>
        <div className="text-[10px] text-zinc-300">A sample lead as a web post or file drop, with their field names in yellow</div>
      </div>
      {!data ? <div className="label-mono p-3">Loading…</div> : (
        <>
          {data.clash && (
            <div className="flex gap-1.5 border-b-2 border-black bg-red-50 px-3 py-2 text-[11px] text-red-700" data-testid="lead-share-clash">
              <WarningCircle size={14} className="shrink-0" weight="bold" />
              Two fields would both be sent as &ldquo;{data.clash}&rdquo; — give one of them a different name.
            </div>
          )}
          <dl className="max-h-[45vh] divide-y divide-zinc-200 overflow-y-auto text-[12px]">
            {data.rows.map((r) => <Row key={r.key || `custom-${r.name}`} r={r} />)}
          </dl>
          {data.left_out.length > 0 && (
            <div className="border-t-2 border-black bg-zinc-50 px-3 py-2 text-[11px]" data-testid="lead-share-preview-left-out">
              <b>Left out:</b> {data.left_out.join(", ")}
            </div>
          )}
          <SampleDownload body={JSON.parse(sig)} destId={destId} />
        </>
      )}
    </aside>
  );
}
